import Box from "@/common/components/Box";
import Button from "@/common/components/Button";
import GoBackButton from "@/common/components/GoBackButton";
import { ImageWithPlaceholder } from "@/common/components/ImageWithPlaceholder";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import Spinner from "@/common/components/Spinner";
import { useGoBack } from "@/common/hooks/useGoBack";
import { FormTransformers } from "@/common/utils/formTransformers";
import { AdditionalDocuments } from "@/features/privateCompanies/components/createBranchForm/additionalDocuments";
import { BranchInformation } from "@/features/privateCompanies/components/createBranchForm/branchInformation";
import CardConfiguration from "@/features/privateCompanies/components/createBranchForm/cardConfiguration";
import { ConsumptionRestriction } from "@/features/privateCompanies/components/createBranchForm/consumptionRestriction";
import { CreditConfiguration } from "@/features/privateCompanies/components/createBranchForm/creditConfiguration";
import { EstablishmentRestriction } from "@/features/privateCompanies/components/createBranchForm/establishmentRestriction";
import { FiscalDocumentInformation } from "@/features/privateCompanies/components/createBranchForm/fiscalDocumentInformation";
import { GeneralInformation } from "@/features/privateCompanies/components/createBranchForm/generalInformation";
import { MasterClientUser } from "@/features/privateCompanies/components/createBranchForm/masterClientUser";
import { PeriodRestriction } from "@/features/privateCompanies/components/createBranchForm/periodRestriction";
import { useCompany } from "@/features/privateCompanies/hooks/useCompany";
import { useCompanyBranch } from "@/features/privateCompanies/hooks/useCompanyBranch";
import useUpdateCompanyBranch from "@/features/privateCompanies/hooks/useUpdateCompanyBranch";
import { CompanyBranchValidationSchema, getCompanyBranchSchema } from "@/features/privateCompanies/schemas/createBranch.schema";
import { companyBranchErrorsTransformer } from "@/features/privateCompanies/services/companyBranchTransformer";
import { companyBranchDefaultValues } from "@/features/privateCompanies/utils/companyBranchDefaultValues";
import usePrivateCompanyLead from "@/features/privateCompanyLeadsFunnel/hooks/usePrivateCompanyLead";
import { usePublicCompanyLead } from "@/features/publicCompanyLeadsFunnel/hooks/usePublicCompanyLead";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { BriefcaseBusinessIcon, ChevronsRight } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { FormProvider, SubmitHandler, useForm, useWatch } from "react-hook-form";
import { useParams } from "react-router-dom";

const Update = () => {
  const { companyId, id: branchId } = useParams();
  const goBack = useGoBack();
  const queryClient = useQueryClient();

  const [file, setFile] = useState<File>();
  const [messages, setMessages] = useState<string[] | boolean>(false);

  const { data: companyData } = useCompany(companyId || "");
  const { data: branchData, isPending: branchDataIsPending } = useCompanyBranch(branchId || "");

  const { data: privateCompanyLead } = usePrivateCompanyLead(
    companyData?.company_lead_id || '',
    companyData?.kind === 'private_company' && !!companyData?.company_lead_id
  )

  const { data: publicCompanyLead } = usePublicCompanyLead(
    companyData?.company_lead_id || '',
    companyData?.kind === 'public_company' && !!companyData?.company_lead_id
  )

  const consumptionControlledBy = companyData?.kind === 'private_company'
    ? privateCompanyLead?.contract?.consumption_controlled_by
    : publicCompanyLead?.contract?.consumption_controlled_by;

  const schema = useMemo(() => getCompanyBranchSchema(consumptionControlledBy), [consumptionControlledBy]);

  const formMethods = useForm<CompanyBranchValidationSchema>({
    resolver: zodResolver(schema),
    defaultValues: companyBranchDefaultValues({ branchData, controlledBy: consumptionControlledBy }),
  });

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    setError,
    clearErrors,
    reset,
    getValues,
    resetField,
    formState: { errors },
  } = formMethods;

  const { mutate: updateCompanyBranch, isPending } = useUpdateCompanyBranch({
    branchId,
    branchData,
    controlledBy: consumptionControlledBy,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['companyBranch', branchId] });
      queryClient.invalidateQueries({ queryKey: ['clientCompaniesAvailableToAllocateInBranchesSummary'] })
      goBack({ fallback: `/companies/${companyId}/branches/${branchId}` });
    },
    onError: (error) => {
      FormTransformers.errorsTransformer({
        setError,
        setMessages,
        transformer: companyBranchErrorsTransformer(),
      })(error);
    }
  })

  const onSubmit: SubmitHandler<CompanyBranchValidationSchema> = async (data) => {
    updateCompanyBranch({ form: data, file });
  };


  const selectedParent = useWatch({
    control,
    name: "parent.value",
  });

  useEffect(() => {
    if (!branchDataIsPending) {
      reset({
        ...companyBranchDefaultValues({ branchData, controlledBy: consumptionControlledBy }),
      });
    }
  }, [branchDataIsPending]);

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex justify-between items-center mb-6">
          <div className="flex gap-2 items-center">
            <GoBackButton fallback={`/companies/${companyId}/branches/${branchId}`} />
            <div className="flex gap-2 items-center">
              <ImageWithPlaceholder
                className="size-8"
                iconClassName="size-6"
                placeholderIcon={BriefcaseBusinessIcon}
                url={companyData?.general_information?.company_logo?.url}
                alt={companyData?.general_information?.fantasy_name}
              />
              <p className="text-white-400 font-normal">{companyData?.general_information?.fantasy_name}</p>
            </div>
            <ChevronsRight color="#ffffffb3" />
            <h2 className="text-xl font-bold">{branchData?.display_name}</h2>
          </div>
          <Button type="submit" name="submit" className="shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]">
            {isPending ? <Spinner className="h-6 mx-[23px] fill-white-800" /> : "Confirmar"}
          </Button>
        </div>
        <Box className="flex flex-col gap-4 shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)] overflow-y-auto h-[calc(100vh-128px)]">
          <p className="text-lg font-bold">Dados da filial</p>
          <BranchInformation
            branchId={branchId}
            register={register}
            errors={errors}
            control={control}
            companyId={companyId}
            showParentIdField={!!branchData?.parent_id}
            parentId={companyId}
            billBy={
              companyData?.kind === 'private_company'
                ? privateCompanyLead?.contract?.bill_by
                : publicCompanyLead?.contract?.bill_by
            }
          />
          <GeneralInformation
            companyId={companyId}
            clearErrors={clearErrors}
            resetField={resetField}
            file={file}
            setFile={setFile}
            companyData={companyData}
            branchData={branchData}
            register={register}
            errors={errors}
            control={control}
            watch={watch}
            setValue={setValue}
            setError={setError}
            parentId={selectedParent}
            disableCnpj={!selectedParent}
            reset={reset}
            getValues={getValues}
          />
          <CardConfiguration
            register={register}
            setValue={setValue}
            errors={errors}
          />
          <MasterClientUser companyId={companyId} errors={errors} control={control} />
          <CreditConfiguration
            register={register}
            control={control}
            watch={watch}
            errors={errors}
            companyData={companyData}
            branchData={branchData}
            consumptionControlledBy={consumptionControlledBy}
            contractProductCategories={
              companyData?.kind === 'public_company'
                ? publicCompanyLead?.contract?.contract_product_categories
                : undefined
            }
          />
          <EstablishmentRestriction
            control={control}
            register={register}
            watch={watch}
            errors={errors}
            disabled={isPending}
          />
          <ConsumptionRestriction disabled={isPending} />
          <PeriodRestriction
            control={control}
            register={register}
            watch={watch}
            errors={errors}
            disabled={isPending}
          />
          <FiscalDocumentInformation
            control={control}
            errors={errors}
            fiscal_document_information={branchData?.fiscal_document_information}
            disabled={isPending}
          />
          <AdditionalDocuments
            disabled={isPending}
          />
        </Box>

        {messages && (
          <ModalMessageSimple
            messages={messages}
            setMessages={setMessages}
            isError
          />
        )}
      </form>
    </FormProvider>
  );
};

export default Update;
